import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Table as TableIcon, 
  Search,
  ChevronLeft,
  Plus,
  Trash2,
  Loader2,
  Play,
  Users
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const getStatusBadge = (status) => {
  switch (status) {
    case 'registration': return { text: 'Anmeldung', className: 'bg-purple-500 text-white' };
    case 'in_progress': return { text: 'Läuft', className: 'bg-amber-500 text-white' };
    case 'completed': return { text: 'Beendet', className: 'bg-slate-500 text-white' };
    default: return { text: status, className: 'bg-slate-500 text-white' };
  }
};

export default function AdminLeagues() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLeague, setNewLeague] = useState({
    name: '',
    description: '',
    season: '',
    entry_fee: 100,
    prize_pool: 1000,
    max_teams: 10,
    team_size: 5,
    matches_per_team: 2,
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    fetchLeagues();
  }, [user, navigate]);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues');
      setLeagues(response.data || []);
    } catch (error) {
      console.error('Failed to fetch leagues:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newLeague.name || !newLeague.season) {
      toast.error('Name und Saison sind erforderlich');
      return;
    }

    setCreating(true);
    try {
      const params = new URLSearchParams({
        name: newLeague.name,
        description: newLeague.description || '',
        season: newLeague.season,
        entry_fee: newLeague.entry_fee.toString(),
        prize_pool: newLeague.prize_pool.toString(),
        max_teams: newLeague.max_teams.toString(),
        team_size: newLeague.team_size.toString(),
        matches_per_team: newLeague.matches_per_team.toString(),
      });

      await api.post(`/admin/leagues?${params.toString()}`);
      toast.success('Liga erstellt!');
      setCreateOpen(false);
      setNewLeague({
        name: '', description: '', season: '',
        entry_fee: 100, prize_pool: 1000, max_teams: 10, team_size: 5, matches_per_team: 2,
      });
      await fetchLeagues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreating(false);
    }
  };

  const handleStart = async (leagueId) => {
    try {
      await api.post(`/leagues/${leagueId}/start`);
      toast.success('Liga gestartet!');
      await fetchLeagues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Starten');
    }
  };

  const handleDelete = async (leagueId, leagueName) => {
    if (!window.confirm(`Liga "${leagueName}" wirklich löschen?`)) return;

    try {
      await api.delete(`/admin/leagues/${leagueId}`);
      toast.success('Liga gelöscht');
      await fetchLeagues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const filteredLeagues = leagues.filter(l =>
    l.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-leagues">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-2xl text-tunis-blue">
              Ligen verwalten
            </h1>
            <p className="text-muted-foreground">{leagues.length} Ligen</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-purple-500 hover:bg-purple-600 text-white">
                <Plus className="w-5 h-5 mr-2" />
                Neue Liga
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Neue Liga erstellen</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={newLeague.name}
                    onChange={(e) => setNewLeague(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Tunis Premier League"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Saison *</Label>
                  <Input
                    value={newLeague.season}
                    onChange={(e) => setNewLeague(prev => ({ ...prev, season: e.target.value }))}
                    placeholder="z.B. 2025 Frühjahr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Beschreibung</Label>
                  <Textarea
                    value={newLeague.description}
                    onChange={(e) => setNewLeague(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Ligabeschreibung..."
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Eintritt (TND)</Label>
                    <Input
                      type="number"
                      value={newLeague.entry_fee}
                      onChange={(e) => setNewLeague(prev => ({ ...prev, entry_fee: parseFloat(e.target.value) }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preisgeld (TND)</Label>
                    <Input
                      type="number"
                      value={newLeague.prize_pool}
                      onChange={(e) => setNewLeague(prev => ({ ...prev, prize_pool: parseFloat(e.target.value) }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Max. Teams</Label>
                    <Select
                      value={newLeague.max_teams.toString()}
                      onValueChange={(value) => setNewLeague(prev => ({ ...prev, max_teams: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[6, 8, 10, 12, 16, 20].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Teamgröße</Label>
                    <Select
                      value={newLeague.team_size.toString()}
                      onValueChange={(value) => setNewLeague(prev => ({ ...prev, team_size: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[3, 4, 5, 6, 7, 11].map(n => (
                          <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Runden</Label>
                    <Select
                      value={newLeague.matches_per_team.toString()}
                      onValueChange={(value) => setNewLeague(prev => ({ ...prev, matches_per_team: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Einfach</SelectItem>
                        <SelectItem value="2">Hin/Rück</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-purple-500 hover:bg-purple-600" disabled={creating}>
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Liga erstellen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Liga suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
          />
        </div>

        {/* League Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLeagues.map((league) => {
            const statusBadge = getStatusBadge(league.status);
            const leader = league.standings?.[0];
            return (
              <Card key={league.id} className="border-slate-200 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-purple-500 to-pink-500" />
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Badge className={statusBadge.className}>{statusBadge.text}</Badge>
                      <Badge variant="outline" className="ml-2">{league.season}</Badge>
                    </div>
                    <div className="flex gap-1">
                      {league.status === 'registration' && league.team_count >= 2 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-green-600 hover:bg-green-50"
                          onClick={() => handleStart(league.id)}
                        >
                          <Play className="w-4 h-4" />
                        </Button>
                      )}
                      {league.status !== 'in_progress' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(league.id, league.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <h3 className="font-bold text-lg text-tunis-blue mb-2">{league.name}</h3>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                    <Users className="w-4 h-4" />
                    {league.team_count}/{league.max_teams} Teams
                  </div>
                  {leader && league.status !== 'registration' && (
                    <div className="p-2 bg-amber-50 rounded-lg border border-amber-200 text-sm mb-3">
                      <span className="text-amber-700">
                        🥇 {leader.team_name} ({leader.points} Pkt.)
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-100">
                    <div className="text-sm">
                      Eintritt: <span className="font-bold">{formatCurrency(league.entry_fee)}</span>
                    </div>
                    <div className="text-sm">
                      Preis: <span className="font-bold text-purple-600">{formatCurrency(league.prize_pool)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredLeagues.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <TableIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-muted-foreground">Keine Ligen gefunden</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
